import { useTheme } from 'styled-components';
import { decimal, label, monthTick, projectDate } from '@entities/ksp/format';
import type { KspAction, KspWorkspace } from '@entities/ksp/types';
import { Button } from '@shared/ui/Button';
import { KspSummary } from '@widgets/ksp-workspace/Summary';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line as ChartLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, Muted, Notice, Row, Small } from '../styles';
import { PagedTable } from '../components/CollectionTable';

export interface PlanFactTabProps {
  workspace: KspWorkspace;
  permission: (action?: KspAction) => boolean;
  createDocument: (kind: string) => void;
}

export function PlanFactTab({
  workspace: d,
  permission,
  createDocument,
}: PlanFactTabProps) {
  const theme = useTheme();
  // План — синий, факт и прогноз — терракота (прогноз пунктиром): пара проверена на
  // различимость при дальтонизме в обеих темах.
  const plan = theme.name === 'dark' ? '#3987e5' : '#2a78d6';
  const fact = theme.colors.primary;
  const timezone = d.settings?.timezone ?? 'Europe/Moscow';
  return (
    <>
      <KspSummary data={d.summary} />
      <Card>
        <h3>Выполнение по версиям объёмов</h3>
        <Muted>
          Базовый график и текущий утверждённый объём сохраняют отдельные знаменатели.
          Несогласованный объём новой версии не меняет принятый показатель.
        </Muted>
        <PagedTable
          rows={d.activities}
          columns={[
            { name: 'Работа', value: (activity) => `${activity.code} · ${activity.name}` },
            {
              name: 'Объём базового графика',
              value: (activity) =>
                `${decimal(activity.baseline_quantity)} ${activity.baseline_unit ?? ''}`,
            },
            {
              name: 'Факт к базовому объёму',
              value: (activity) => (
                <>
                  {decimal(activity.baseline_fact_pct, ' %')}
                  {activity.baseline_reason && (
                    <Small>{label(activity.baseline_reason)}</Small>
                  )}
                </>
              ),
            },
            {
              name: 'Текущий утверждённый объём',
              value: (activity) =>
                `${decimal(activity.current_quantity)} ${activity.current_unit ?? ''}`,
            },
            {
              name: 'Факт к текущему объёму',
              value: (activity) => (
                <>
                  {decimal(activity.current_fact_pct, ' %')}
                  {activity.current_reason && <Small>{label(activity.current_reason)}</Small>}
                </>
              ),
            },
          ]}
        />
      </Card>
      <Card>
        <Row>
          <h3>Накопительные кривые</h3>
          <Button
            disabled={!permission() || !d.summary}
            onClick={() => createDocument('deviation_act')}
          >
            Акт фиксации отклонений
          </Button>
        </Row>
        {d.summary?.curves?.map((curve, i) => (
          <Card key={i}>
            <Muted>
              {label(curve.method)} · {curve.unit === '%' ? '% от объёма' : curve.unit} ·{' '}
              {label(d.summary?.coverage ?? 'unknown')}
            </Muted>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart
                data={curve.points.map((point) => ({
                  date: point.date,
                  plan: point.baseline == null ? null : Number(point.baseline),
                  actual: point.actual == null ? null : Number(point.actual),
                  forecast: point.forecast == null ? null : Number(point.forecast),
                }))}
              >
                <CartesianGrid stroke={theme.colors.border} vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value: string) => monthTick(value, timezone)}
                  stroke={theme.colors.text.muted}
                  tick={{ fontSize: 11 }}
                />
                <YAxis stroke={theme.colors.text.muted} tick={{ fontSize: 11 }} />
                <Tooltip
                  labelFormatter={(value) => projectDate(String(value), timezone)}
                  contentStyle={{
                    background: theme.colors.backgroundElevated,
                    border: `1px solid ${theme.colors.borderStrong}`,
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area
                  dataKey="plan"
                  name="Базовый план"
                  fill={plan}
                  stroke={plan}
                  fillOpacity={0.12}
                  strokeWidth={2}
                  connectNulls={false}
                />
                <ChartLine
                  dataKey="actual"
                  name="Принятый факт"
                  stroke={fact}
                  strokeWidth={2.5}
                  dot={false}
                  connectNulls={false}
                />
                <ChartLine
                  dataKey="forecast"
                  name="Прогноз"
                  stroke={fact}
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  dot={false}
                  connectNulls={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
            <Small>
              Точность показателя сохраняется в таблицах и выгрузках; линия — визуальное
              представление. Пустые окна не подменяются нулём.
            </Small>
          </Card>
        ))}
        {!d.summary?.curves?.length && (
          <Notice>
            Кривые пока не рассчитаны: нужны утверждённые базовый график и веса работ, а для
            факта — принятые рапорты.
          </Notice>
        )}
      </Card>
    </>
  );
}
